import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme';
import MainLayout from './components/MainLayout';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import OwnersPage from './pages/OwnersPage';
import AccountsPage from './pages/AccountsPage';
import TransactionsPage from './pages/TransactionsPage';
import ScheduledPaymentsPage from './pages/ScheduledPaymentsPage';
import SecretsPage from './pages/SecretsPage';
import LoginPage from './pages/LoginPage';
import AccessDeniedPage from './pages/AccessDeniedPage';
import { AuthProvider, useAuth } from './auth/AuthContext';
import TwapWalletPage from './pages/TwapWalletPage';
import WalletKeyGeneratorPage from './pages/WalletKeyGeneratorPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import AdminUsersPage from './pages/AdminUsersPage';
import ChangePasswordPage from './pages/ChangePasswordPage';

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user.admin) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
};

const RootRedirect: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return null;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to={user.admin ? '/owners' : '/twap'} replace />;
};

const App = () => (
  <ThemeProvider theme={theme}>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <RootRedirect />
              </RequireAuth>
            }
          />
          <Route
            element={
              <RequireAdmin>
                <MainLayout>
                  <Outlet />
                </MainLayout>
              </RequireAdmin>
            }
          >
            <Route path="/owners" element={<OwnersPage />} />
            <Route path="/users" element={<AdminUsersPage />} />
            <Route path="/accounts" element={<AccountsPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/scheduled-payments" element={<ScheduledPaymentsPage />} />
            <Route path="/secrets" element={<SecretsPage />} />
          </Route>
          <Route
            element={
              <RequireAuth>
                <MainLayout>
                  <Outlet />
                </MainLayout>
              </RequireAuth>
            }
          >
            <Route path="/twap" element={<TwapWalletPage />} />
            <Route path="/wallet-key-generator" element={<WalletKeyGeneratorPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
          </Route>
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  </ThemeProvider>
);

export default App;
